import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Mail,
  Phone,
  Calendar,
  MapPin,
  GraduationCap,
  User,
  Briefcase,
  Building,
} from "lucide-react";
import { getTeacherById } from "../../services/teachers";
import LoadingSpinner from "../common/LoadingSpinner";

interface TeacherDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
  onEdit?: () => void;
}

const TeacherDetailsModal: React.FC<TeacherDetailsModalProps> = ({
  isOpen,
  onClose,
  teacherId,
  onEdit,
}) => {
  const {
    data: teacherResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: () => getTeacherById(teacherId),
    enabled: !!teacherId && isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (!isOpen) return null;

  const teacher = teacherResponse?.data;
  const userInfo = teacher?.userInfo;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Teacher Details
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {onEdit && (
              <button
                onClick={onEdit}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2">
                <GraduationCap className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error Loading Teacher
              </h3>
              <p className="text-gray-600">
                Unable to load teacher details. Please try again.
              </p>
            </div>
          ) : null}

          {teacher && (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="flex items-center gap-4">
                {userInfo?.profileDetails?.profilePicture ? (
                  <img
                    src={userInfo.profileDetails.profilePicture}
                    alt={`${userInfo?.firstName} ${userInfo?.lastName}`}
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center ${userInfo?.profileDetails?.profilePicture ? 'hidden' : ''}`}>
                  <span className="text-blue-600 text-xl font-semibold">
                    {userInfo?.firstName?.charAt(0) || ""}
                    {userInfo?.lastName?.charAt(0) || ""}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {userInfo?.firstName} {userInfo?.lastName}
                  </h3>
                  <p className="text-gray-600">
                    {teacher.designation || "Teacher"}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (userInfo as any)?.status === "active" ||
                        teacher.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {(userInfo as any)?.status === "active" ||
                      teacher.isActive
                        ? "Active"
                        : "Inactive"}
                    </span>
                    <span className="text-sm text-gray-500">
                      ID: {teacher.employeeId}
                    </span>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userInfo?.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">{userInfo.email}</p>
                      </div>
                    </div>
                  )}
                  {userInfo &&
                    typeof userInfo === "object" &&
                    userInfo.profileDetails?.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-medium">
                            {userInfo.profileDetails.phone}
                          </p>
                        </div>
                      </div>
                    )}
                  {userInfo &&
                    typeof userInfo === "object" &&
                    userInfo.profileDetails?.dateOfBirth && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Date of Birth</p>
                          <p className="font-medium">
                            {new Date(
                              userInfo.profileDetails.dateOfBirth
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    )}
                  {userInfo &&
                    typeof userInfo === "object" &&
                    userInfo.profileDetails?.gender && (
                      <div className="flex items-center gap-3">
                        <User className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Gender</p>
                          <p className="font-medium capitalize">
                            {userInfo.profileDetails.gender}
                          </p>
                        </div>
                      </div>
                    )}
                  {userInfo &&
                    typeof userInfo === "object" &&
                    userInfo.profileDetails?.address && (
                      <div className="flex items-start gap-3 md:col-span-2">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Address</p>
                          <p className="font-medium">
                            {typeof userInfo.profileDetails.address === "string"
                              ? userInfo.profileDetails.address
                              : typeof userInfo.profileDetails.address ===
                                "object"
                              ? `${
                                  userInfo.profileDetails.address.street || ""
                                } ${
                                  userInfo.profileDetails.address.city || ""
                                } ${
                                  userInfo.profileDetails.address.state || ""
                                } ${
                                  userInfo.profileDetails.address.country || ""
                                }`.trim()
                              : "Address not available"}
                          </p>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Professional Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-medium">{teacher.department}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Designation</p>
                    <p className="font-medium">{teacher.designation}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Employment Type</p>
                    <p className="font-medium capitalize">
                      {teacher.employmentType?.replace("_", " ") || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Joining Date</p>
                    <p className="font-medium">
                      {teacher.joiningDate
                        ? new Date(teacher.joiningDate).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Employment Status</p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (userInfo as any)?.status === "active" ||
                        teacher.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {(userInfo as any)?.status === "active" ||
                      teacher.isActive
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Education & Qualifications */}
              {teacher.qualification && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <GraduationCap className="h-5 w-5" />
                    Education & Qualifications
                  </h4>

                  {/* Education */}
                  {teacher.qualification.education &&
                    teacher.qualification.education.length > 0 && (
                      <div className="mb-6">
                        <h5 className="font-medium text-gray-800 mb-3">
                          Education
                        </h5>
                        <div className="space-y-3">
                          {teacher.qualification.education.map(
                            (edu: any, index: number) => (
                              <div
                                key={index}
                                className="border-l-4 border-blue-500 pl-4"
                              >
                                <p className="font-medium text-gray-900">
                                  {edu.degree}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {edu.institution} ({edu.year})
                                </p>
                                {edu.grade && (
                                  <p className="text-sm text-gray-600">
                                    Grade: {edu.grade}
                                  </p>
                                )}
                                {edu.major && (
                                  <p className="text-sm text-gray-600">
                                    Major: {edu.major}
                                  </p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* Certifications */}
                  {teacher.qualification.certifications &&
                    teacher.qualification.certifications.length > 0 && (
                      <div className="mb-6">
                        <h5 className="font-medium text-gray-800 mb-3">
                          Certifications
                        </h5>
                        <div className="space-y-3">
                          {teacher.qualification.certifications.map(
                            (cert: any, index: number) => (
                              <div
                                key={index}
                                className="border-l-4 border-green-500 pl-4"
                              >
                                <p className="font-medium text-gray-900">
                                  {cert.name}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {cert.issuingOrganization}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Issued:{" "}
                                  {cert.issueDate
                                    ? new Date(
                                        cert.issueDate
                                      ).toLocaleDateString()
                                    : "N/A"}
                                  {cert.expiryDate &&
                                    ` | Expires: ${new Date(
                                      cert.expiryDate
                                    ).toLocaleDateString()}`}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* Experience */}
                  {teacher.qualification.experience && (
                    <div>
                      <h5 className="font-medium text-gray-800 mb-3">
                        Experience
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {teacher.qualification.experience.totalYears && (
                          <div>
                            <p className="text-sm text-gray-600">Total Years</p>
                            <p className="font-medium">
                              {teacher.qualification.experience.totalYears}{" "}
                              years
                            </p>
                          </div>
                        )}
                      </div>

                      {teacher.qualification.experience.previousPositions &&
                        teacher.qualification.experience.previousPositions
                          .length > 0 && (
                          <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">
                              Previous Positions
                            </p>
                            {teacher.qualification.experience.previousPositions.map(
                              (pos: any, index: number) => (
                                <div
                                  key={index}
                                  className="border-l-4 border-purple-500 pl-4"
                                >
                                  <p className="font-medium text-gray-900">
                                    {pos.position}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {pos.institution}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {pos.startDate
                                      ? new Date(
                                          pos.startDate
                                        ).toLocaleDateString()
                                      : "N/A"}{" "}
                                    -
                                    {pos.endDate
                                      ? new Date(
                                          pos.endDate
                                        ).toLocaleDateString()
                                      : "Present"}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}

              {/* Subjects */}
              {teacher.subjects && teacher.subjects.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Subjects
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {teacher.subjects.map((subject: any, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        {subject.courseId || subject.name || "Subject"}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Salary Information */}
              {teacher.salary && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Salary Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {teacher.salary.basicSalary && (
                      <div>
                        <p className="text-sm text-gray-600">Basic Salary</p>
                        <p className="font-medium">
                          Ksh {teacher.salary.basicSalary.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {teacher.salary.paymentSchedule && (
                      <div>
                        <p className="text-sm text-gray-600">
                          Payment Schedule
                        </p>
                        <p className="font-medium capitalize">
                          {teacher.salary.paymentSchedule}
                        </p>
                      </div>
                    )}
                    {teacher.salary.allowances &&
                      teacher.salary.allowances.length > 0 && (
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-600 mb-2">
                            Allowances
                          </p>
                          <div className="space-y-1">
                            {teacher.salary.allowances.map(
                              (allowance: any, index: number) => (
                                <div
                                  key={index}
                                  className="flex justify-between text-sm"
                                >
                                  <span>{allowance.type}</span>
                                  <span>
                                    ₹{allowance.amount.toLocaleString()}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Emergency Contact */}
              {teacher.emergencyContact && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Emergency Contact
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {teacher.emergencyContact.name && (
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-medium">
                          {teacher.emergencyContact.name}
                        </p>
                      </div>
                    )}
                    {teacher.emergencyContact.relationship && (
                      <div>
                        <p className="text-sm text-gray-600">Relationship</p>
                        <p className="font-medium">
                          {teacher.emergencyContact.relationship}
                        </p>
                      </div>
                    )}
                    {teacher.emergencyContact.phone && (
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium">
                          {teacher.emergencyContact.phone}
                        </p>
                      </div>
                    )}
                    {teacher.emergencyContact.email && (
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium">
                          {teacher.emergencyContact.email}
                        </p>
                      </div>
                    )}
                    {teacher.emergencyContact.address && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-600">Address</p>
                        <p className="font-medium">
                          {teacher.emergencyContact.address}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bank Details */}
              {teacher.bankDetails && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Bank Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {teacher.bankDetails.accountHolderName && (
                      <div>
                        <p className="text-sm text-gray-600">Account Holder</p>
                        <p className="font-medium">
                          {teacher.bankDetails.accountHolderName}
                        </p>
                      </div>
                    )}
                    {teacher.bankDetails.accountNumber && (
                      <div>
                        <p className="text-sm text-gray-600">Account Number</p>
                        <p className="font-medium">
                          ****{teacher.bankDetails.accountNumber.slice(-4)}
                        </p>
                      </div>
                    )}
                    {teacher.bankDetails.bankName && (
                      <div>
                        <p className="text-sm text-gray-600">Bank</p>
                        <p className="font-medium">
                          {teacher.bankDetails.bankName}
                        </p>
                      </div>
                    )}
                    {teacher.bankDetails.branchName && (
                      <div>
                        <p className="text-sm text-gray-600">Branch</p>
                        <p className="font-medium">
                          {teacher.bankDetails.branchName}
                        </p>
                      </div>
                    )}
                    {teacher.bankDetails.routingNumber && (
                      <div>
                        <p className="text-sm text-gray-600">Routing/IFSC</p>
                        <p className="font-medium">
                          {teacher.bankDetails.routingNumber}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Teaching Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Teaching Assignments
                </h4>

                {/* Assigned Classes and Subjects */}
                <div className="mb-6">
                  <h5 className="font-medium text-gray-900 mb-3">
                    Class Assignments
                  </h5>
                  {teacher.classes && teacher.classes.length > 0 ? (
                    <div className="space-y-3">
                      {teacher.classes.map(
                        (classAssignment: any, index: number) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg border p-3"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-medium text-gray-900">
                                  {typeof classAssignment.classId === "object"
                                    ? classAssignment.classId?.name ||
                                      "Unknown Class"
                                    : "Unknown Class"}
                                </span>
                                {classAssignment.isClassTeacher && (
                                  <span className="ml-2 inline-flex px-1 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                                    Class Teacher
                                  </span>
                                )}
                                {typeof classAssignment.classId === "object" &&
                                  classAssignment.classId?.grade && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Grade: {classAssignment.classId.grade}
                                    </p>
                                  )}
                                {classAssignment.academicTermId && (
                                  <p className="text-xs text-gray-600">
                                    Term: {classAssignment.academicTermId}
                                  </p>
                                )}
                              </div>
                              {classAssignment.courses &&
                                classAssignment.courses.length > 0 && (
                                  <div className="text-right">
                                    <p className="text-xs text-gray-600 mb-1">
                                      Subjects:
                                    </p>
                                    <div className="flex flex-wrap gap-1 justify-end">
                                      {classAssignment.courses
                                        .slice(0, 3)
                                        .map(
                                          (
                                            course: any,
                                            courseIndex: number
                                          ) => (
                                            <span
                                              key={courseIndex}
                                              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
                                            >
                                              {typeof course === "object"
                                                ? course.name
                                                : course}
                                            </span>
                                          )
                                        )}
                                      {classAssignment.courses.length > 3 && (
                                        <span className="text-xs text-gray-500">
                                          +{classAssignment.courses.length - 3}{" "}
                                          more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border p-4 text-center text-gray-500">
                      No class assignments
                    </div>
                  )}
                </div>

                {/* General Subjects */}
                {teacher.subjects && teacher.subjects.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-medium text-gray-900 mb-3">
                      General Subject Expertise
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {teacher.subjects.map(
                        (subjectItem: any, index: number) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg border p-2"
                          >
                            <span className="font-medium text-gray-900">
                              {subjectItem.courseId?.name || "Unknown Subject"}
                            </span>
                            {subjectItem.isPrimary && (
                              <span className="ml-2 inline-flex px-1 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800">
                                Primary
                              </span>
                            )}
                            {subjectItem.level && (
                              <p className="text-xs text-gray-600 mt-1">
                                Level: {subjectItem.level}
                              </p>
                            )}
                            {subjectItem.yearsTeaching > 0 && (
                              <p className="text-xs text-gray-600">
                                Experience: {subjectItem.yearsTeaching} years
                              </p>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Summary Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-gray-600">Classes Assigned</p>
                    <p className="font-medium">
                      {teacher.classes?.length || 0} classes
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Subjects Teaching</p>
                    <p className="font-medium">
                      {teacher.subjects?.length || 0} subjects
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Class Teacher</p>
                    <p className="font-medium">
                      {teacher.classes?.some((cls: any) => cls.isClassTeacher)
                        ? "Yes"
                        : "No"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="text-sm text-gray-500 border-t pt-4">
                <div className="flex justify-between">
                  <span>
                    Created: {new Date(teacher.createdAt).toLocaleDateString()}
                  </span>
                  <span>
                    Updated: {new Date(teacher.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherDetailsModal;
